/**
 * Application configuration.
 *
 * Resolution order for each key:
 *   1. `window.__APP_CONFIG__` — set at runtime (e.g. injected by Docker entrypoint).
 *   2. `import.meta.env.VITE_*` — baked in at build time from `.env`.
 *   3. Sensible defaults.
 */

interface RuntimeAppConfig {
  /** Kai-App Entra app (client) id. (SEC-201 rename of ENTRA_CLIENT_ID.) */
  KAI_APP_CLIENT_ID?: string;
  /** Shared Kai Entra tenant id. (SEC-201 rename of ENTRA_TENANT_ID.) */
  KAI_TENANT_ID?: string;
  /** @deprecated SEC-201 — legacy name, read as a fallback during rollout. */
  ENTRA_CLIENT_ID?: string;
  /** @deprecated SEC-201 — legacy name, read as a fallback during rollout. */
  ENTRA_TENANT_ID?: string;
  ENTRA_REDIRECT_URI?: string;
  ENTRA_POST_LOGOUT_REDIRECT_URI?: string;
  ENTRA_SCOPES?: string;
  APP_NAME?: string;
  /**
   * Environment label reported to LogRocket (e.g. "staging", "production").
   * Falls back to "default" when unset.
   */
  ENV_NAME?: string;
  /** Kai-API base URL, e.g. http://localhost:3000/api/v1 */
  API_BASE_URL?: string;
  /** Optional API key sent as x-api-key when the API requires it. */
  API_KEY?: string;
  /** Tenant slug forwarded to the API (multi-tenant support). */
  API_TENANT?: string;
  /**
   * MSAL scope for the Kai-API Bearer token, e.g.
   * `api://<kai-api-client-id>/access_as_user`. Set when the SPA and kai-api
   * are separate app registrations. Falls back to the SPA's own client id.
   */
  API_SCOPE?: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: RuntimeAppConfig;
  }
}

const runtime = (typeof window !== 'undefined' && window.__APP_CONFIG__) || {};

// Returns the first non-empty (trimmed) value. Variadic so a key can fall back
// across new + legacy names, e.g. runtime KAI_*, build-time VITE_KAI_*, then the
// legacy runtime/VITE_ENTRA_* names during the SEC-201 rollout. Guards on
// typeof: window.__APP_CONFIG__ is runtime-injected, so a misconfigured
// config.js could hold non-string values and calling .trim() would throw.
const pick = (...values: (string | undefined)[]): string => {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

export interface AppConfig {
  appName: string;
  /** Environment label reported to LogRocket. Defaults to "default". */
  envName: string;
  entraClientId: string;
  entraTenantId: string;
  entraRedirectUri: string;
  entraPostLogoutRedirectUri: string;
  entraScopes: string[];
  /** Kai-API base URL (no trailing slash). */
  apiBaseUrl: string;
  /** Optional `x-api-key` value when Kai-API is in protected mode. */
  apiKey: string;
  /** Optional tenant slug, forwarded as the `x-tenant` header. */
  apiTenant: string;
  /**
   * MSAL scope used to acquire a Bearer token for Kai-API. Configured via
   * `API_SCOPE` (runtime) or `VITE_API_SCOPE` (build time) when the SPA and
   * kai-api are separate app registrations; otherwise derived from
   * `entraClientId` as `api://<client-id>/access_as_user`.
   */
  apiScope: string;
}

const defaultRedirect =
  typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173';

// The Kai-App Entra app (client) ID, resolved once so it can seed both the MSAL
// config and the derived Kai-API scope below. SEC-201: prefer the product-specific
// KAI_APP_CLIENT_ID / VITE_KAI_APP_CLIENT_ID, falling back to the legacy
// ENTRA_CLIENT_ID / VITE_ENTRA_CLIENT_ID during rollout.
const entraClientId = pick(
  runtime.KAI_APP_CLIENT_ID,
  import.meta.env.VITE_KAI_APP_CLIENT_ID,
  runtime.ENTRA_CLIENT_ID,
  import.meta.env.VITE_ENTRA_CLIENT_ID,
);

// Kai-API exposes a single delegated permission, `access_as_user`, on its app
// registration. When kai-api is a separate registration from the SPA (staging
// and production), the token audience must be kai-api's client id, so API_SCOPE
// is set explicitly to `api://<kai-api-client-id>/access_as_user`. When unset
// (local/dev, or a shared registration) fall back to deriving the scope from
// the SPA's own client id.
const API_DELEGATED_PERMISSION = 'access_as_user';
const resolveApiScope = (): string => {
  const configured = pick(runtime.API_SCOPE, import.meta.env.VITE_API_SCOPE);
  if (configured) return configured;
  return entraClientId ? `api://${entraClientId}/${API_DELEGATED_PERMISSION}` : '';
};

// API base. A relative value (e.g. "/api/v1") resolves against the current
// origin so the SPA calls its own host — supports same-origin tenant subdomains
// (<tenant>.kaihealth.ai/api/* -> kai-api) without CORS. Absolute values pass
// through unchanged (e.g. the localhost dev default).
const apiOrigin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
const resolveApiBaseUrl = (): string => {
  const raw =
    pick(runtime.API_BASE_URL, import.meta.env.VITE_API_BASE_URL) || 'http://localhost:3000/api/v1';
  // Resolve against the origin so any relative form (/api/v1, api/v1, ./api/v1)
  // becomes absolute; an already-absolute raw ignores the base.
  return new URL(raw, apiOrigin).toString().replace(/\/+$/, '');
};

export const appConfig: AppConfig = {
  appName: pick(runtime.APP_NAME, import.meta.env.VITE_APP_NAME) || 'Kai Health',
  envName: pick(runtime.ENV_NAME, import.meta.env.VITE_ENV_NAME) || 'default',
  entraClientId,
  entraTenantId:
    pick(
      runtime.KAI_TENANT_ID,
      import.meta.env.VITE_KAI_TENANT_ID,
      runtime.ENTRA_TENANT_ID,
      import.meta.env.VITE_ENTRA_TENANT_ID,
    ) || 'common',
  entraRedirectUri:
    pick(runtime.ENTRA_REDIRECT_URI, import.meta.env.VITE_ENTRA_REDIRECT_URI) || defaultRedirect,
  entraPostLogoutRedirectUri:
    pick(
      runtime.ENTRA_POST_LOGOUT_REDIRECT_URI,
      import.meta.env.VITE_ENTRA_POST_LOGOUT_REDIRECT_URI,
    ) || defaultRedirect,
  entraScopes: (pick(runtime.ENTRA_SCOPES, import.meta.env.VITE_ENTRA_SCOPES) || 'User.Read')
    .split(/\s+/)
    .filter(Boolean),
  apiBaseUrl: resolveApiBaseUrl(),
  apiKey: pick(runtime.API_KEY, import.meta.env.VITE_API_KEY),
  apiTenant: pick(runtime.API_TENANT, import.meta.env.VITE_API_TENANT),
  apiScope: resolveApiScope(),
};

export const isAuthConfigured = (): boolean =>
  Boolean(appConfig.entraClientId) && Boolean(appConfig.entraTenantId);
